import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Badge, Button, Card, Container, Spinner } from 'react-bootstrap';

import AdminFeedbackFilters, {
  AdminFeedbackFiltersValue,
  DEFAULT_FILTERS,
} from '../components/feedback/admin/AdminFeedbackFilters';
import AdminFeedbackStats from '../components/feedback/admin/AdminFeedbackStats';
import AdminFeedbackTable from '../components/feedback/admin/AdminFeedbackTable';
import FeedbackDetailsModal from '../components/feedback/admin/FeedbackDetailsModal';
import { useModal } from '../context/ModalContext';
import useDebouncedValue from '../hooks/useDebouncedValue';
import { feedbackApi } from '../services/api';
import {
  Feedback,
  FeedbackAdminScopeOption,
  FeedbackStats,
} from '../types';
import { exportFeedbacksToExcel } from '../utils/feedbackExport';
import './AdminFeedbacks.css';

const PAGE_LIMIT = 25;
const FILTERS_DEBOUNCE_MS = 350;

interface ApiParams {
  scope?: string;
  min_rating?: number;
  max_rating?: number;
  date_from?: string;
  date_to?: string;
  is_anonymous?: boolean;
}

const buildApiParams = (filters: AdminFeedbackFiltersValue): ApiParams => {
  const params: ApiParams = {};
  if (filters.scopeKey && filters.scopeKey !== 'ALL') {
    params.scope = filters.scopeKey;
  }
  if (typeof filters.minRating === 'number') params.min_rating = filters.minRating;
  if (typeof filters.maxRating === 'number') params.max_rating = filters.maxRating;
  if (filters.dateFrom) params.date_from = `${filters.dateFrom}T00:00:00`;
  if (filters.dateTo) params.date_to = `${filters.dateTo}T23:59:59`;
  if (filters.anonymous === 'yes') params.is_anonymous = true;
  if (filters.anonymous === 'no') params.is_anonymous = false;
  return params;
};

const AdminFeedbacks: React.FC = () => {
  const { showAlert, showConfirm } = useModal();

  const [filters, setFilters] = useState<AdminFeedbackFiltersValue>(DEFAULT_FILTERS);
  const debouncedFilters = useDebouncedValue(filters, FILTERS_DEBOUNCE_MS);

  const [skip, setSkip] = useState<number>(0);
  const [items, setItems] = useState<Feedback[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [stats, setStats] = useState<FeedbackStats | null>(null);
  const [scopeOptions, setScopeOptions] = useState<FeedbackAdminScopeOption[]>([]);
  const [loadingScopes, setLoadingScopes] = useState<boolean>(false);
  const [loadingList, setLoadingList] = useState<boolean>(false);
  const [loadingStats, setLoadingStats] = useState<boolean>(false);
  const [exporting, setExporting] = useState<boolean>(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedFeedback, setSelectedFeedback] = useState<Feedback | null>(null);

  const isMountedRef = useRef<boolean>(true);
  const inFlightListRef = useRef<AbortController | null>(null);
  const inFlightStatsRef = useRef<AbortController | null>(null);
  const exportAbortRef = useRef<AbortController | null>(null);

  // Reseta a paginação quando os filtros mudam (já debounced).
  useEffect(() => {
    setSkip(0);
  }, [debouncedFilters]);

  // Carrega opções de escopo (alinhadas ao formulário de feedback).
  useEffect(() => {
    isMountedRef.current = true;
    const controller = new AbortController();
    setLoadingScopes(true);
    feedbackApi
      .getAdminScopes(controller.signal)
      .then((data) => {
        if (!isMountedRef.current) return;
        setScopeOptions(data.options);
      })
      .catch((err: unknown) => {
        if (err instanceof Error && err.name === 'CanceledError') return;
        // Falha no catálogo não bloqueia a tela; select fica com Todos/Plataforma se vier vazio.
      })
      .finally(() => {
        if (!isMountedRef.current) return;
        setLoadingScopes(false);
      });
    return () => {
      isMountedRef.current = false;
      controller.abort();
      inFlightListRef.current?.abort();
      inFlightStatsRef.current?.abort();
      exportAbortRef.current?.abort();
    };
  }, []);

  // Busca lista + stats em paralelo. Usa AbortController para cancelar requisições
  // anteriores quando filtros/skip mudam, evitando race conditions e flood.
  useEffect(() => {
    const apiParams = buildApiParams(debouncedFilters);

    inFlightListRef.current?.abort();
    inFlightStatsRef.current?.abort();

    const listController = new AbortController();
    const statsController = new AbortController();
    inFlightListRef.current = listController;
    inFlightStatsRef.current = statsController;

    setError(null);
    setLoadingList(true);
    setLoadingStats(true);

    const listPromise = feedbackApi
      .list({ ...apiParams, skip, limit: PAGE_LIMIT }, listController.signal)
      .then((res) => {
        if (!isMountedRef.current) return;
        setItems(res.items);
        setTotal(res.total);
      })
      .catch((err: unknown) => {
        if (err instanceof Error && err.name === 'CanceledError') return;
        if (!isMountedRef.current) return;
        setItems([]);
        setTotal(0);
        setError('Não foi possível carregar a lista de feedbacks. Tente novamente.');
      })
      .finally(() => {
        if (!isMountedRef.current) return;
        setLoadingList(false);
      });

    const statsPromise = feedbackApi
      .getStats(apiParams, statsController.signal)
      .then((res) => {
        if (!isMountedRef.current) return;
        setStats(res);
      })
      .catch((err: unknown) => {
        if (err instanceof Error && err.name === 'CanceledError') return;
        if (!isMountedRef.current) return;
        setStats(null);
      })
      .finally(() => {
        if (!isMountedRef.current) return;
        setLoadingStats(false);
      });

    return () => {
      // Não awaiting; somente abort. Isso garante que callbacks pendentes não
      // tentem setState em árvore desmontada (eslint-disable: promise sem await OK).
      void listPromise;
      void statsPromise;
      listController.abort();
      statsController.abort();
    };
  }, [debouncedFilters, skip]);

  const handleFiltersChange = useCallback((next: AdminFeedbackFiltersValue) => {
    setFilters(next);
  }, []);

  const handlePageChange = useCallback((nextSkip: number) => {
    setSkip(nextSkip);
    // Sobe pra topo da listagem ao mudar de página (UX comum em data tables).
    requestAnimationFrame(() => {
      const tableEl = document.getElementById('admin-feedbacks-list');
      tableEl?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, []);

  const handleView = useCallback((item: Feedback) => {
    setSelectedFeedback(item);
  }, []);

  const handleCloseDetails = useCallback(() => {
    setSelectedFeedback(null);
  }, []);

  const handleDelete = useCallback(
    async (id: number) => {
      const confirmed = await showConfirm({
        title: 'Excluir feedback',
        message: `Tem certeza que deseja excluir o feedback #${id}? Essa ação não pode ser desfeita.`,
        variant: 'danger',
        confirmLabel: 'Excluir',
        cancelLabel: 'Cancelar',
      });
      if (!confirmed) return;

      setDeletingId(id);
      const controller = new AbortController();
      try {
        await feedbackApi.remove(id, controller.signal);
        if (!isMountedRef.current) return;
        setItems((prev) => prev.filter((it) => it.id !== id));
        setTotal((prev) => Math.max(0, prev - 1));
        setSelectedFeedback((prev) => (prev?.id === id ? null : prev));
        showAlert({
          variant: 'success',
          title: 'Feedback removido',
          message: `O feedback #${id} foi removido com sucesso.`,
        });
      } catch (err: unknown) {
        if (err instanceof Error && err.name === 'CanceledError') return;
        showAlert({
          variant: 'danger',
          title: 'Erro ao remover',
          message: 'Não foi possível remover o feedback. Tente novamente.',
        });
      } finally {
        if (isMountedRef.current) setDeletingId(null);
      }
    },
    [showAlert, showConfirm],
  );

  const handleExport = useCallback(async () => {
    if (exporting) return;
    setExporting(true);
    exportAbortRef.current?.abort();
    const controller = new AbortController();
    exportAbortRef.current = controller;

    try {
      const apiParams = buildApiParams(debouncedFilters);
      // Para o export, busca uma página grande (até 200, que é o máximo do backend)
      // — para volumes maiores, recomenda-se exportação paginada via /api/feedbacks/export
      // que já existe no backend. Aqui mantemos simples e seguro.
      const res = await feedbackApi.list(
        { ...apiParams, skip: 0, limit: 200 },
        controller.signal,
      );
      if (!isMountedRef.current) return;
      if (res.items.length === 0) {
        showAlert({
          variant: 'warning',
          title: 'Nada para exportar',
          message: 'Ajuste os filtros para que haja resultados antes de exportar.',
        });
        return;
      }
      exportFeedbacksToExcel({ feedbacks: res.items, stats });
      showAlert({
        variant: 'success',
        title: 'Exportação concluída',
        message: `${res.items.length} feedback${res.items.length === 1 ? '' : 's'} exportado${res.items.length === 1 ? '' : 's'} com sucesso.${res.total > res.items.length ? ` (Mostrando ${res.items.length} de ${res.total} totais — refine os filtros para exportar mais.)` : ''}`,
      });
    } catch (err: unknown) {
      if (err instanceof Error && err.name === 'CanceledError') return;
      showAlert({
        variant: 'danger',
        title: 'Erro ao exportar',
        message: 'Não foi possível gerar a planilha. Tente novamente em instantes.',
      });
    } finally {
      if (isMountedRef.current) setExporting(false);
    }
  }, [debouncedFilters, exporting, showAlert, stats]);

  const isFetching = loadingList || loadingStats;
  const hasActiveFilters = useMemo(() => {
    return JSON.stringify(filters) !== JSON.stringify(DEFAULT_FILTERS);
  }, [filters]);

  return (
    <Container fluid className="admin-feedbacks py-4">
      <Card className="shadow-sm border-0">
        <Card.Header className="admin-feedbacks__header">
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
            <div>
              <h4 className="mb-0">
                <i className="bi bi-clipboard-data me-2" aria-hidden="true"></i>
                Feedbacks (administração)
              </h4>
              <small className="admin-feedbacks__subtitle">
                Estatísticas, filtros e listagem detalhada das avaliações da plataforma
              </small>
            </div>
            <div className="d-flex flex-wrap gap-2 align-items-center">
              {hasActiveFilters && (
                <Badge bg="info" className="admin-feedbacks__badge">
                  <i className="bi bi-funnel-fill me-1" aria-hidden="true"></i>
                  Filtros ativos
                </Badge>
              )}
              <Button
                variant="success"
                onClick={handleExport}
                disabled={exporting || total === 0}
              >
                {exporting ? (
                  <>
                    <Spinner as="span" size="sm" animation="border" />
                    <span className="ms-2">Exportando...</span>
                  </>
                ) : (
                  <>
                    <i className="bi bi-file-earmark-excel-fill me-2" aria-hidden="true"></i>
                    Exportar Excel
                  </>
                )}
              </Button>
            </div>
          </div>
        </Card.Header>

        <Card.Body>
          <section className="mb-4" aria-label="Filtros">
            <AdminFeedbackFilters
              value={filters}
              onChange={handleFiltersChange}
              scopeOptions={scopeOptions}
              loadingScopes={loadingScopes}
              totalResults={total}
              isFetching={isFetching}
            />
          </section>

          <section className="mb-4" aria-label="Estatísticas">
            <AdminFeedbackStats stats={stats} loading={loadingStats} />
          </section>

          {error && (
            <Alert variant="danger" className="mb-3">
              <i className="bi bi-exclamation-triangle-fill me-2" aria-hidden="true"></i>
              {error}
            </Alert>
          )}

          <section id="admin-feedbacks-list" aria-label="Listagem de feedbacks">
            <h5 className="admin-feedbacks__list-title">
              <i className="bi bi-list-ul me-2" aria-hidden="true"></i>
              Feedbacks
            </h5>
            <AdminFeedbackTable
              items={items}
              total={total}
              skip={skip}
              limit={PAGE_LIMIT}
              loading={loadingList}
              onPageChange={handlePageChange}
              onView={handleView}
              onDelete={handleDelete}
              deletingId={deletingId}
            />
          </section>
        </Card.Body>
      </Card>

      <FeedbackDetailsModal
        show={selectedFeedback !== null}
        feedback={selectedFeedback}
        onClose={handleCloseDetails}
      />
    </Container>
  );
};

export default AdminFeedbacks;
